import { useState, useEffect, useRef } from 'react';

const MUSIC_TRACKS = [
'/music/main-theme.mp3',
  '/music/secondary-theme.mp3'
];

const DEFAULT_VOLUME = 0.3;

export const useBackgroundMusic = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isInitializedRef = useRef(false);

  const playNextTrack = () => {
    if (!audioRef.current) return;
    
    setCurrentTrackIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % MUSIC_TRACKS.length;
      audioRef.current!.src = MUSIC_TRACKS[nextIndex];
      if (isPlaying) {
        audioRef.current!.play().catch(console.warn);
      }
      return nextIndex;
    });
  };

  // Initialize audio on mount
  useEffect(() => {
    audioRef.current = document.getElementById('bgMusic') as HTMLAudioElement;
    if (audioRef.current && !isInitializedRef.current) {
      audioRef.current.volume = DEFAULT_VOLUME;
      audioRef.current.src = MUSIC_TRACKS[currentTrackIndex];
      audioRef.current.addEventListener('ended', playNextTrack);
      isInitializedRef.current = true;

      // Try to play immediately
      const startMusic = () => {
        if (!audioRef.current) return;
        
        const playPromise = audioRef.current.play();
        if (playPromise) {
          playPromise.catch(() => {
            // Auto-play was prevented, we'll wait for user interaction
            const handleFirstInteraction = () => {
              if (audioRef.current) {
                audioRef.current.play().then(() => {
                  setIsPlaying(true);
                });
              }
              document.removeEventListener('click', handleFirstInteraction);
              document.removeEventListener('touchstart', handleFirstInteraction);
              document.removeEventListener('keydown', handleFirstInteraction);
            };

            document.addEventListener('click', handleFirstInteraction);
            document.addEventListener('touchstart', handleFirstInteraction);
            document.addEventListener('keydown', handleFirstInteraction);
          });
        }
      };

      startMusic();
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', playNextTrack);
      }
    };
  }, []);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isPlaying ? DEFAULT_VOLUME : 0;
    }
  }, [isPlaying]);

  const togglePlay = () => {
    setIsPlaying(prev => !prev);
  };

  return {
    isPlaying,
    togglePlay,
    currentTrack: MUSIC_TRACKS[currentTrackIndex]
  };
};

export const useButtonSound = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = document.getElementById('clickSound') as HTMLAudioElement;
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
    }
  }, []);

  const playSound = () => {
    if (!audioRef.current) return;
    
    audioRef.current.currentTime = 0;
    const playPromise = audioRef.current.play();
    if (playPromise) {
      playPromise.catch((error) => {
        console.warn('Button sound error:', error);
      });
    }
  };

  return { playSound };
};